import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { environment } from 'src/environments/environment';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';
import { InconsistencyInterface } from 'src/app/util/interfaces/myAssetInterface';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { ciInterface2 } from 'src/app/util/interfaces/ci';
import { ApiResponse } from 'src/app/util/interfaces/ApiResponse';
import { mspFileInterface } from 'src/app/util/interfaces/mspFile';
import { logInterface } from 'src/app/util/interfaces/log.interface';
import { inconsistencyFollowUp } from '../components/inconsistencies-follow-up/data/inconsistencies-follow-up.interface';
import { ciTypesInterface } from 'src/app/util/interfaces/ciTypes';
import { inconsistenciesCiListI } from '../components/inconsistencies-ci-list/data/inconsistencies-ci-list.interface';
import { mspTableHeaderI } from 'src/app/page_component_item/msp-table/msp-table.component';
@Injectable({
	providedIn: 'root',
})
export class InconsistenciesService {
	private headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});
	private availableInconsistenciesTableHeaderUrl = environment.baseApiUrl + 'myasset/inconsistencies/header/available';
	private inconsistenciesTableHeaderUrl = environment.baseApiUrl + 'myasset/inconsistencies/header';
	private searchValues = environment.baseApiUrl + 'myasset/inconsistencies';
	private InconsistencyUrl = environment.baseApiUrl + 'inconsistency';
	private getInconsistencyByIdUrl = environment.baseApiUrl + 'inconsistency/{id}';
	private bondedCiByInconsistencyUrl = environment.baseApiUrl + 'inconsistency/{id}/bondedCi';
	private getCmdbListUrl = environment.baseApiUrl + 'cmdb/cmdbList';
	private getRegisterFormUrl = environment.baseApiUrl + 'inconsistency/registerForm';
	private getStatusesUrl = environment.baseApiUrl + 'inconsistency/statuses';
	private getAllowedCiTypesUrl = environment.baseApiUrl + 'inconsistency/allowedCiTypes';
	private getLogsUrl = environment.baseApiUrl + 'inconsistency/logs/{id}';
	private getInconsistenciesTypesUrl = environment.baseApiUrl + 'myasset/inconsistenciesTypes';
	private followUpUrl = environment.baseApiUrl + 'inconsistency/followUp/{id}';

	constructor(
		private AuthLoginService: AuthLoginService,
		private http: HttpClient
	) {}

	getAvailableInconsistenciesTableHeaders(): Observable<genericFormI['items'][]> {
		return this.http.get<genericFormI['items'][]>(this.availableInconsistenciesTableHeaderUrl, { headers: this.headers });
	}

	getUserInconsistencyTableHeaders(): Observable<mspTableHeaderI[]> {
		return this.http.get<mspTableHeaderI[]>(this.inconsistenciesTableHeaderUrl, { headers: this.headers });
	}

	updateUserInconsistencyTableHeaders(columns: genericFormI['values']): Observable<ApiResponse<string[]>> {
		return this.http.put<ApiResponse<string[]>>(this.inconsistenciesTableHeaderUrl, { columns }, { headers: this.headers });
	}

	searchCITable(
		page: number,
		searchParameters: { [k: string]: string[] },
		sortBy: string,
		sortDown: boolean,
		recount: boolean,
		pageSize?: string
	): Observable<{
		count: number;
		ciArr: any[];
	}> {
		const headerParameter = {
			page: page,
			general: searchParameters.general,
			sortBy: sortBy,
			sortDown: sortDown,
			recount: recount,
		};

		if (pageSize != undefined) headerParameter['pageSize'] = pageSize;

		for (const key of Object.keys(searchParameters)) {
			headerParameter[key] = searchParameters[key].join(',');
		}

		return this.http.get<{
			count: number;
			ciArr: any[];
		}>(this.searchValues, {
			headers: this.headers,
			params: headerParameter,
		});
	}

	getInconsistencyById(id?: string): Observable<InconsistencyInterface[]> {
		if (id == undefined) {
			return of([]);
		}
		return this.http.get<InconsistencyInterface[]>(`${this.getInconsistencyByIdUrl.replace('{id}', id.toString())}`, {
			headers: this.headers,
		});
	}

	getBondedCiByInconsistency(id?: string): Observable<ApiResponse<inconsistenciesCiListI[]>> {
		if (id == undefined) {
			return of({ data: [] } as ApiResponse<inconsistenciesCiListI[]>);
		}
		return this.http.get<ApiResponse<inconsistenciesCiListI[]>>(`${this.bondedCiByInconsistencyUrl.replace('{id}', id.toString())}`, {
			headers: this.headers,
		});
	}

	postBondedCiByInconsistency(id: string, idCi: string): Observable<any> {
		return this.http.post<any>(
			`${this.bondedCiByInconsistencyUrl.replace('{id}', id.toString())}`,
			{ cmdb_id: idCi },
			{ headers: this.headers }
		);
	}

	deleteCiByInconsistency(id: string, idCi: string): Observable<any> {
		return this.http.delete<any>(`${this.bondedCiByInconsistencyUrl.replace('{id}', id.toString())}?cmdb_id=${idCi}`, {
			headers: this.headers,
		});
	}

	// API POST /api/inconsistency/ - Cria uma nova inconsistência
	createInconsistency(data: InconsistencyInterface): Observable<any> {
		return this.http.post<any>(this.InconsistencyUrl, data, { headers: this.headers });
	}

	updateInconsistency(data: InconsistencyInterface): Observable<any> {
		return this.http.put<any>(this.InconsistencyUrl, data, { headers: this.headers });
	}

	getCmdbList(searchParameter: { field: string; value: string[] }): Observable<ciInterface2> {
		return this.http.post<ciInterface2[]>(this.getCmdbListUrl, searchParameter, { headers: this.headers }).pipe(
			map((response) => {
				return response.length > 0 ? response[0] : null;
			})
		);
	}

	getRegisterForm(): Observable<genericFormI[]> {
		return this.http.get<genericFormI[]>(this.getRegisterFormUrl, { headers: this.headers });
	}

	// API GET /inconsistency/statuses - Busca todos os status disponíveis
	getStatuses(): Observable<{ id: number; label: string; translated: string; name: string }[]> {
		return this.http.get<{ id: number; label: string; translated: string; name: string }[]>(this.getStatusesUrl, {
			headers: this.headers,
		});
	}

	getAllowedCiTypes(): Observable<string> {
		return this.http
			.get<Array<Pick<ciTypesInterface, 'id'> & { ci_type?: number }>>(this.getAllowedCiTypesUrl, {
				headers: this.headers,
			})
			.pipe(map((response) => response.map((item) => item.ci_type ?? item.id).join(',')));
	}

	// API GET /myasset/inconsistenciesTypes - Lista todos os tipos de inconsistências
	getInconsistenciesTypes(): Observable<any> {
		return this.http.get<any>(this.getInconsistenciesTypesUrl, { headers: this.headers });
	}

	getInconsistencyLogs(id: string | number): Observable<any[]> {
		const url = this.getLogsUrl.replace('{id}', id.toString());

		// Campos que devem ser REMOVIDOS dos changes
		const fieldsToRemove = new Set([
			'inconsistency_type_id',
			'status',
			'validator',
			'id',
			'last_update_date',
			'creation_date',
			'automatic',
			'treatment_responsible',
			'stock_responsible',
			'location',
			'id_customer',
			'created_by',
			'action',
			'approved_by',
			'approved_by_name',
			'approved_date',
		]);

		// Labels para os campos
		// const fieldLabels = {
		//   'status_name': 'lbl_status',
		//   'validator_name': 'lbl_geral_validador',
		//   'treatment_responsible_name': 'lbl_treatment_responsible',
		//   'priority': 'lbl_priority',
		//   'inconsistency_reason': 'lbl_inconsistency_reason',
		//   'location_name': 'lbl_location',
		//   'last_update_date': 'lbl_last_updated',
		//   'customer_name': 'lbl_customer',
		//   'stock_responsible_name': 'lbl_stock_responsible'
		// };

		// Usamos <any[]> no get, pois a API retorna o 'baseline' como string
		return this.http.get<any[]>(url, { headers: this.headers }).pipe(
			map((logs) => {
				// Para cada log na resposta, fazemos a conversão
				return logs.map((log) => {
					let filteredChanges = null;

					// Se existe changes, remove campos indesejados (blacklist)
					if (log.changes) {
						filteredChanges = {};
						for (const key of Object.keys(log.changes)) {
							if (!fieldsToRemove.has(key)) {
								filteredChanges[key] = {
									...log.changes[key],
									label: log.changes[key].label || key, // Usa o label do objeto ou o próprio key como fallback
								};
							}
						}

						// Se após o filtro não restou nenhum campo, deixa como null
						if (Object.keys(filteredChanges).length === 0) {
							filteredChanges = null;
						}
					}

					return {
						...log,
						baseline: log.baseline,
						changes: filteredChanges,
					};
				});
			})
		);
	}

	getFollowUp(id: string): Observable<ApiResponse<inconsistencyFollowUp[]>> {
		const url = this.followUpUrl.replace('{id}', id.toString());
		return this.http.get<ApiResponse<inconsistencyFollowUp[]>>(url, { headers: this.headers });
	}

	postFollowUp(id: string, logText: string, logFile?: mspFileInterface): Observable<any> {
		const url = this.followUpUrl.replace('{id}', id.toString());
		return this.http.post<any>(url, { logText, logFile }, { headers: this.headers });
	}
}
